import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTeacher } from '../../context/TeacherContext';
import { Box, CircularProgress, Typography } from '@mui/material';
import apiAxiosInstance from '../../utils/axiosInstance';

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setTeacher } = useTeacher();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const token = searchParams.get('token');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        console.error('OAuth error:', errorParam);
        setError('Authentication failed');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      if (token) {
        try {
          // Store the token
        localStorage.setItem('token', token);

          // Verify token minimally
          const verifyRes = await apiAxiosInstance.get('/auth/verify');
          if (!verifyRes.data?.teacher?._id) {
            throw new Error('Token verified but no teacher id returned');
          }

          // Hydrate full teacher profile
          const profileRes = await apiAxiosInstance.get('/auth/teacher-profile');
          const teacher = profileRes.data?.teacher;
          if (teacher) {
            setTeacher(teacher);

            // Route based on real profile data
            if (!teacher.gradeLevel || !teacher.teachingStandards?.length) {
              navigate('/complete-profile');
            } else {
              navigate('/edu-chat');
            }
          } else {
            throw new Error('No teacher data received');
          }
        } catch (error: any) {
          console.error('Error verifying token:', error);
          
          // Handle timeout errors specifically
          if (error.message?.includes('timeout') || error.message?.includes('timed out')) {
            setError('Authentication is taking longer than expected. Please wait...');
            // Don't remove token on timeout - retry once
            setTimeout(async () => {
              try {
                const retryRes = await apiAxiosInstance.get('/auth/verify');
                if (retryRes.data?.teacher?._id) {
                  // Continue with profile loading
                  const profileRes = await apiAxiosInstance.get('/auth/teacher-profile');
                  const teacher = profileRes.data?.teacher;
                  if (teacher) {
                    setTeacher(teacher);
                    if (!teacher.gradeLevel || !teacher.teachingStandards?.length) {
                      navigate('/complete-profile');
                    } else {
                      navigate('/edu-chat');
                    }
                    return;
                  }
                }
              } catch (retryError) {
                console.error('Retry failed:', retryError);
              }
              // If retry fails, show error and redirect
              setError('Authentication failed. Please try again.');
              localStorage.removeItem('token');
              setTimeout(() => navigate('/login'), 2000);
            }, 3000);
            return;
          }
          
          setError('Failed to authenticate user');
          localStorage.removeItem('token');
          setTimeout(() => navigate('/login'), 2000);
        }
      } else {
        setError('No authentication token received');
        setTimeout(() => navigate('/login'), 2000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setTeacher]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      {error ? (
        <>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting to login...
          </Typography>
        </>
      ) : (
        <>
          <CircularProgress size={40} />
          <Typography variant="h6">
            Completing sign in...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we set up your account
          </Typography>
        </>
      )}
    </Box>
  );
};

export default AuthCallback; 